import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Eye, FileDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import { Skeleton } from '../../components/ui/skeleton';
import { StatusBadge } from '../../components/common/Badges';
import api from '../../lib/api';
import { downloadFile } from '../../lib/download';
import { errMsg, fmtMoney, fmtNumber } from '../../lib/format';

export default function MobileCotizacionDetallePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: q, isLoading } = useQuery({
    queryKey: ['quotation-full', id],
    queryFn: async () => (await api.get(`/quotations/${id}/full`)).data,
  });

  if (isLoading) return <div className="space-y-3"><Skeleton className="h-16" /><Skeleton className="h-64" /></div>;
  if (!q) return <div className="text-muted-foreground">Cotización no encontrada</div>;

  const moneda = q.moneda || 'MXN';
  const t = q.totals || {};

  const exportPdf = async () => {
    try {
      await downloadFile(`/quotations/${id}/export-pdf`, `${q.quotation_number}.pdf`);
      toast.success('PDF descargado');
    } catch (e) {
      toast.error(errMsg(e, 'No se pudo descargar el PDF'));
    }
  };

  return (
    <div className="space-y-4" data-testid="mobile-cotizacion-detalle">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate('/m/cotizaciones')} data-testid="mobile-back-button">
          <ArrowLeft size={17} />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[12px] text-muted-foreground">{q.quotation_number}</span>
            <StatusBadge status={q.status} />
          </div>
          <h2 className="text-[16px] font-semibold truncate">{q.nombre}</h2>
        </div>
        <Button variant="outline" size="icon" className="h-9 w-9" onClick={exportPdf} data-testid="mobile-export-pdf-button" title="Descargar PDF">
          <FileDown size={16} />
        </Button>
      </div>

      <p className="text-[12px] text-muted-foreground flex items-center gap-1"><Eye size={12} /> Solo lectura · Para editar usa la versión de escritorio</p>

      <Card className="border-border shadow-[var(--shadow-elev-1)]" data-testid="mobile-totals-card">
        <CardHeader className="pb-2"><CardTitle className="text-[15px]">Totales</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Subtotal conceptos</span><span className="money">{fmtMoney(t.subtotal, moneda)}</span></div>
          {t.global_cascade && (
            <>
              <div className="flex justify-between"><span className="text-muted-foreground">+ Indirectos ({fmtNumber(q.global_indirect_pct, 2)}%)</span><span className="money">{fmtMoney(t.global_cascade.indirect_amount, moneda)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">+ Financiamiento ({fmtNumber(q.global_financing_pct, 2)}%)</span><span className="money">{fmtMoney(t.global_cascade.financing_amount, moneda)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">+ Utilidad ({fmtNumber(q.global_utility_pct, 2)}%)</span><span className="money">{fmtMoney(t.global_cascade.utility_amount, moneda)}</span></div>
              <div className="flex justify-between font-medium"><span className="text-muted-foreground">Subtotal antes de IVA</span><span className="money">{fmtMoney(t.subtotal_before_iva, moneda)}</span></div>
            </>
          )}
          <div className="flex justify-between"><span className="text-muted-foreground">IVA ({fmtNumber(q.iva_percentage, 0)}%)</span><span className="money">{fmtMoney(t.iva_amount, moneda)}</span></div>
          <Separator />
          <div className="flex justify-between text-base font-semibold"><span>Total</span><span className="money text-primary" data-testid="mobile-total">{fmtMoney(t.total, moneda)}</span></div>
        </CardContent>
      </Card>

      {(q.groups || []).map((g) => (
        <Card key={g.id} className="border-border shadow-[var(--shadow-elev-1)]">
          <CardHeader className="pb-2"><CardTitle className="text-[14px]">{g.nombre_grupo}</CardTitle></CardHeader>
          <CardContent className="space-y-2.5">
            {(g.concepts || []).length === 0 && <p className="text-[12px] text-muted-foreground">Sin conceptos</p>}
            {(g.concepts || []).map((c) => (
              <div key={c.id} className="border border-border rounded-lg px-3 py-2.5 bg-background/40">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium leading-snug">{c.descripcion}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{c.numero_concepto} · {fmtNumber(c.cantidad)} {c.unidad} × {fmtMoney(c.calculated_unit_price, moneda)}</div>
                  </div>
                  <span className="money text-[13px] font-semibold shrink-0">{fmtMoney(c.calculated_total, moneda)}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
